/**
 * 二维码配置
 * 管理重试次数、延迟等常量
 * 
 * 重要规则：
 * 1. 禁止外链二维码：支付类二维码只能"本地编码"或"同源图片接口"显示
 * 2. 支付宝qr_code字符串必须通过前端本地编码渲染，不能直接作为img src
 * 3. 避免CORS/302/反盗链问题
 */
export const QR_CODE_CONFIG = {
  // 最大重试次数
  MAX_RETRIES: 3,

  // 重试延迟基数（秒）
  RETRY_DELAY_BASE: 1,

  // 二维码刷新间隔（毫秒）
  REFRESH_INTERVAL: 5000,

  // 支付状态检查间隔（毫秒）
  STATUS_CHECK_INTERVAL: 3000,

  // 二维码生成超时时间（毫秒）
  GENERATION_TIMEOUT: 10000,

  // 错误状态显示时间（毫秒）
  ERROR_DISPLAY_TIME: 5000,

  // 二维码尺寸配置
  QR_CODE_SIZE: {
    WIDTH: 128,
    HEIGHT: 128,
    MARGIN: 2
  },

  // 颜色配置
  COLORS: {
    WECHAT: {
      DARK: '#000000',
      LIGHT: '#FFFFFF'
    },
    ALIPAY: {
      DARK: '#1677FF',
      LIGHT: '#FFFFFF'
    }
  }
};

/**
 * 计算重试延迟时间
 * @param {number} retryCount 重试次数
 * @returns {number} 延迟时间（毫秒）
 */
export const calculateRetryDelay = (retryCount) => {
  return QR_CODE_CONFIG.RETRY_DELAY_BASE * Math.pow(2, retryCount - 1) * 1000;
};

/**
 * 判断错误是否可重试
 * @param {number} status HTTP状态码
 * @param {string} message 错误消息
 * @returns {boolean} 是否可重试
 */
export const isRetriableError = (status, message = '') => {
  // 网络错误通常可重试
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }

  // 超时错误可重试
  if (message.includes('timeout') || message.includes('504')) {
    return true;
  }

  // 业务逻辑错误不可重试
  if (status === 400 || status === 401 || status === 403 || status === 404 || status === 422) {
    return false;
  }

  // 其他错误不可重试
  return false;
};

/**
 * 获取错误提示文案
 * @param {string} code 错误代码
 * @param {boolean} retriable 是否可重试
 * @returns {string} 错误提示文案
 */
export const getErrorMessage = (code, retriable = false) => {
  if (retriable) {
    return '网络暂时不可用，请稍后重试';
  }

  switch (code) {
    case 'PARAM_ERROR':
      return '参数错误，请检查订单信息';
    case 'ORDER_NOT_FOUND':
      return '订单不存在';
    case 'INVALID_ORDER_STATUS':
      return '订单状态不允许支付';
    case 'INVALID_AMOUNT':
      return '订单金额无效';
    case 'ALIPAY_ERROR':
      return '支付宝服务暂时不可用';
    case 'QR_CODE_EMPTY':
      return '二维码生成失败';
    case 'API_ERROR':
      return '支付宝接口调用失败';
    case 'UNKNOWN_ERROR':
      return '未知错误，请稍后重试';
    case '422':
      return '请求参数有误，请检查订单信息';
    default:
      return '支付服务暂时不可用，请稍后重试';
  }
};

/**
 * 验证二维码内容是否合法
 * @param {string} qrCode 二维码内容
 * @returns {boolean} 是否合法
 */
export const isValidQRCode = (qrCode) => {
  if (!qrCode || typeof qrCode !== 'string') {
    return false;
  }
  
  // 支付宝二维码应该是URL格式
  if (qrCode.startsWith('https://qr.alipay.com/')) {
    return true;
  }
  
  // 微信支付二维码应该是weixin://格式
  if (qrCode.startsWith('weixin://')) {
    return true;
  }
  
  return false;
};

/**
 * 获取二维码类型
 * @param {string} qrCode 二维码内容
 * @returns {string} 二维码类型 ('alipay' | 'wechat' | 'unknown')
 */
export const getQRCodeType = (qrCode) => {
  if (!qrCode) return 'unknown';
  
  if (qrCode.startsWith('https://qr.alipay.com/')) {
    return 'alipay';
  }
  
  if (qrCode.startsWith('weixin://')) {
    return 'wechat';
  }
  
  return 'unknown';
};
